import React, { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { supabase } from '../supabase';
import { getGuestName } from '../lib/guestName';
import { REALMS } from '../../../shared/src/realms';
import '../index.css'; // Inherit styles

type Period = 'daily' | 'weekly' | 'alltime';

/**
 * The date window a period key covers, for counting the games behind a board:
 * answers record when they were given, not which board they fed. Boundaries
 * are UTC to match the period keys the server writes.
 */
function periodWindow(period: Period, pKey: string): { from: string; to: string } | null {
  if (period === 'alltime') return null;
  if (period === 'daily') {
    const start = new Date(`${pKey}T00:00:00.000Z`);
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + 86400000);
    return { from: start.toISOString(), to: end.toISOString() };
  }
  // 'IYYY-Www' back to the Monday it starts on: jump to the week's Thursday
  // from 4 January, which ISO guarantees falls in week 1, then step back three
  // days. Doing it any other way drifts at the turn of the year.
  const m = /^(\d{4})-W(\d{2})$/.exec(pKey);
  if (!m) return null;
  const [, year, week] = m;
  const jan4 = new Date(Date.UTC(Number(year), 0, 4));
  const thursday = new Date(jan4.getTime());
  thursday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() || 7) - 4) + (Number(week) - 1) * 7);
  const monday = new Date(thursday.getTime() - 3 * 86400000);
  return { from: monday.toISOString(), to: new Date(monday.getTime() + 7 * 86400000).toISOString() };
}

/**
 * Every row a query matches, a page at a time. The API stops at 1000 rows per
 * request without saying so, and a board read in one go silently loses
 * whoever falls past that line (the all-time RUSH board already has). The page
 * size must not exceed that server cap, or a capped page looks like the last
 * one. Each page is a fresh query and needs a stable order, or pages overlap.
 */
async function readAll(page: (from: number, to: number) => PromiseLike<{ data: any[] | null; error: any }>) {
  const size = 1000;
  const rows: any[] = [];
  for (let from = 0; ; from += size) {
    const { data, error } = await page(from, from + size - 1);
    if (error) return { data: rows, error };
    rows.push(...(data ?? []));
    if (!data || data.length < size) return { data: rows, error: null };
  }
}

export function Leaderboard() {
  const setView = useGameStore((s) => s.setView);
  const nickname = useGameStore((s) => s.nickname);
  const displayNick = (nickname || getGuestName()).toUpperCase();

  const [period, setPeriod] = useState<Period>('daily');
  // TODAY is the honest default but an empty board is a bad first screen
  // (fresh install, quiet day). Until the player picks a period, an empty
  // TODAY falls through to ALL TIME.
  const periodTouched = useRef(false);
  const [realm, setRealm] = useState<string>('GLOBAL'); // GLOBAL or specific realm ID
  // Scores only rank against scores earned under the same rules; each mode
  // is its own board. CLASSIC (pre-mode history) folds into CLIMB, whose
  // recipe those games effectively were. VERSUS holds every two-player room,
  // whatever rules it was played under.
  const [mode, setMode] = useState<'CLIMB' | 'RUSH' | 'SURVIVAL' | 'DAILY' | 'VERSUS'>('CLIMB');
  // One open player card at a time; tapping the row again closes it.
  const [expanded, setExpanded] = useState<string | null>(null);
  const [leaders, setLeaders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [myRank, setMyRank] = useState<number | null>(null);
  const [myScoreData, setMyScoreData] = useState<any>(null);

  // Compute period keys correctly as per DB
  const getPeriodKey = (p: Period) => {
    const d = new Date();
    if (p === 'daily') {
      return d.toISOString().split('T')[0]; // YYYY-MM-DD
    }
    if (p === 'weekly') {
      // Must equal Postgres to_char(now(), 'IYYY') || '-W' || to_char(now(), 'IW')
      // or the weekly board reads a key nobody writes. ISO week: the week
      // belongs to the year of its Thursday; computed in UTC like the server.
      // (The old approximation drifted to next week on Sat/Sun and skipped
      // the zero padding IW carries for weeks 1-9.)
      const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
      t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
      const isoYear = t.getUTCFullYear();
      const week = Math.ceil(((t.getTime() - Date.UTC(isoYear, 0, 1)) / 86400000 + 1) / 7);
      return `${isoYear}-W${String(week).padStart(2, '0')}`;
    }
    return 'alltime';
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setMyRank(null);
    setMyScoreData(null);
    setExpanded(null);

    const fetchLeaderboard = async () => {
      const pKey = getPeriodKey(period);

      // We want to fetch all realms if 'GLOBAL', or specific if not.
      // But the schema implies scores are segmented per realm!
      // To get 'GLOBAL', we either need a specific 'GLOBAL' realm row or we sum them up locally.
      // Easiest is to sum them up in UI!
      // The game count cannot be summed that way: a match writes one row per
      // realm, so GLOBAL would report a three-realm game three times, and it
      // counts matches instead (below). A single realm shows games_played,
      // which is not a game count either: upsert_leaderboard_score bumps it
      // once per answer. Counting games per realm needs the realm of every
      // question a match served, and anon cannot read questions that have
      // since been deactivated, so that view still shows answers.
      const { data, error } = await readAll((from, to) => {
        let query = supabase
          .from('leaderboard')
          .select('nickname, user_id, total_score, games_played, correct_count, total_answers, easy_correct, easy_total, medium_correct, medium_total, hard_correct, hard_total, total_time_ms, best_depth')
          .eq('period', period)
          .eq('period_key', pKey);

        query = mode === 'CLIMB'
          ? query.in('mode', ['CLIMB', 'CLASSIC'])
          : query.eq('mode', mode);

        if (realm !== 'GLOBAL') {
          query = query.eq('realm', realm);
        }

        return query.order('id').range(from, to);
      });

      if (!active) return;
      if (error) {
        console.error('Leaderboard fetch error:', error);
        setLeaders([]);
        setLoading(false);
        return;
      }

      // Grouped by user_id, not by nickname. One row per player now means two
      // guests who pick the same name stay two players instead of merging
      // into one inflated entry.
      let aggregated: Record<string, any> = {};
      data.forEach((row: any) => {
        const nick = String(row.nickname ?? '').toUpperCase();
        // Pre-identity guests all wrote as PLAYER or GUEST; those rows are a
        // mass grave, not people, and nobody can claim them. Hidden, kept.
        if (nick === 'PLAYER' || nick === 'GUEST') return;
        const key = row.user_id ?? nick;
        if (!aggregated[key]) {
          aggregated[key] = {
            user_id: row.user_id,
            scopeGames: 0,
            nickname: row.nickname, total_score: 0, correct_count: 0, total_answers: 0,
            easy_correct: 0, easy_total: 0, medium_correct: 0, medium_total: 0,
            hard_correct: 0, hard_total: 0, total_time_ms: 0, depth: 0,
          };
        }
        const a = aggregated[key];
        a.scopeGames += row.games_played ?? 0;
        a.total_score += row.total_score;
        a.correct_count += row.correct_count;
        a.total_answers += row.total_answers ?? 0;
        a.easy_correct += row.easy_correct ?? 0;
        a.easy_total += row.easy_total ?? 0;
        a.medium_correct += row.medium_correct ?? 0;
        a.medium_total += row.medium_total ?? 0;
        a.hard_correct += row.hard_correct ?? 0;
        a.hard_total += row.hard_total ?? 0;
        a.total_time_ms += row.total_time_ms ?? 0;
        a.depth = Math.max(a.depth, row.best_depth ?? 0);
      });

      // GLOBAL counts matches. A game is a solo match of this board's mode
      // (CLIMB keeps the pre-mode classics, whose mode is null) in which the
      // player answered at least once inside the period; a timeout is an
      // answer too. Answer time is the clock the writer files points under, so
      // a game across midnight counts on both days, as its points do.
      // Two-player rooms count only on VERSUS, whatever their rules.
      // Read from match_players, not player_matches: only match_players has a
      // foreign key to matches, so the mode filter runs in this one request,
      // and player_matches keeps pre-identity games under retired user ids
      // that no match row answers to. No list of player ids in the filter
      // either; past a few hundred players it no longer fits in a URL.
      const playerGames: Record<string, number> = {};
      let gamesKnown = true;
      if (realm === 'GLOBAL' && Object.keys(aggregated).length) {
        const win = periodWindow(period, pKey);
        const { data: seats, error: seatsError } = await readAll((from, to) => {
          let seatQuery = supabase
            .from('match_players')
            .select('user_id, matches!inner(), answers!inner()')
            .eq('matches.is_solo', mode !== 'VERSUS');
          if (mode === 'CLIMB') {
            seatQuery = seatQuery.or('mode.eq.CLIMB,mode.is.null', { referencedTable: 'matches' });
          } else if (mode !== 'VERSUS') {
            seatQuery = seatQuery.eq('matches.mode', mode);
          }
          if (win) seatQuery = seatQuery.gte('answers.submitted_at', win.from).lt('answers.submitted_at', win.to);
          return seatQuery.order('id').range(from, to);
        });
        if (!active) return;
        if (seatsError) {
          console.error('Leaderboard game count error:', seatsError);
          gamesKnown = false;
        }
        seats.forEach((r: any) => {
          playerGames[r.user_id] = (playerGames[r.user_id] ?? 0) + 1;
        });
      }

      // Accuracy exists only where the denominator covers the numerator.
      // Legacy corrects accumulated for weeks before wrong answers were
      // recorded at all, so an all-time row can hold more corrects than
      // counted answers; dividing those would print percentages over 100.
      // Daily and weekly boards start clean immediately (fresh period keys);
      // all-time earns its percentage once real counting overtakes history.
      // SURVIVAL ranks by depth, because depth is that mode's real score;
      // points break ties. Everything else ranks by points.
      const sorted = Object.values(aggregated)
        .map((p: any) => ({
          ...p,
          games: realm !== 'GLOBAL' ? p.scopeGames : gamesKnown ? (playerGames[p.user_id] ?? 0) : null,
          acc: p.total_answers > 0 && p.total_answers >= p.correct_count
            ? Math.round((p.correct_count / p.total_answers) * 100)
            : null,
          avgSec: p.total_answers > 0 && p.total_time_ms > 0
            ? (p.total_time_ms / p.total_answers / 1000)
            : null,
        }))
        .sort((a: any, b: any) => mode === 'SURVIVAL'
          ? (b.depth - a.depth) || (b.total_score - a.total_score)
          : b.total_score - a.total_score);
      
      // Find current user's rank
      const myIndex = sorted.findIndex((p: any) => p.nickname.toUpperCase() === displayNick);
      if (myIndex !== -1) {
        setMyRank(myIndex + 1);
        setMyScoreData(sorted[myIndex]);
      }

      if (period === 'daily' && sorted.length === 0 && !periodTouched.current) {
        setPeriod('alltime');
        return;
      }

      setLeaders(sorted.slice(0, 50)); // Top 50
      setLoading(false);
    };

    fetchLeaderboard();
    return () => { active = false; };
  }, [period, realm, mode]);

  // The stat line answers a different question per mode: CLIMB asks how
  // sharp, RUSH asks how fast, SURVIVAL asks how deep (depth is already the
  // big number there, so its line carries volume and points instead).
  // games is null when its count failed to load; the line leaves it out
  // rather than print a 0 next to real points.
  const statLine = (p: any) => {
    const count = (unit: string) => (p.games === null ? '' : `${p.games} ${unit} · `);
    if (mode === 'SURVIVAL') return `${count('RUNS')}${p.total_score.toLocaleString('en-US')} PTS`;
    if (mode === 'RUSH' && p.avgSec !== null) return `${count('GAMES')}${p.avgSec.toFixed(1)}S AVG`;
    return p.acc !== null
      ? `${count('GAMES')}${p.acc}% ACC`
      : `${count('GAMES')}${p.correct_count} CORRECT`;
  };

  // One band of the expanded card: label, fill bar, and the fraction. The
  // fill wears the challenge hues (green to red), not the mode accent, so
  // the three bars read as a difficulty scale at a glance.
  const BAND_HUES = { easy: '#14b8a6', medium: '#c4973a', hard: '#e0575b' } as const;
  const bandRow = (label: 'easy' | 'medium' | 'hard', correct: number, total: number) => {
    if (total === 0) return null;
    const pct = Math.round((correct / total) * 100);
    return (
      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.12em', color: '#b6b6be', width: 44 }}>
          {label.toUpperCase()}
        </span>
        <div style={{ flex: 1, height: 4, background: '#2C3A58', borderRadius: 2 }}>
          <div style={{ width: `${Math.max(3, pct)}%`, height: '100%', borderRadius: 2, background: BAND_HUES[label] }} />
        </div>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: '#dcdce0', width: 74, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {correct}/{total} · {pct}%
        </span>
      </div>
    );
  };

  const renderBadge = (index: number) => {
    if (index === 0) return <div className="lb-badge gold">1</div>;
    if (index === 1) return <div className="lb-badge silver">2</div>;
    if (index === 2) return <div className="lb-badge bronze">3</div>;
    if (index < 10) return <div className="lb-badge purple">{index + 1}</div>;
    return <div className="lb-badge standard">{index + 1}</div>;
  };

  return (
    <div className="page-profile-compact">
      {/* Compact header: back icon + title on one row (same language as setup) */}
      <div style={{ display: 'flex', alignItems: 'center', height: 40, width: '100%', maxWidth: 400, margin: '4px auto 8px', padding: '0 16px' }}>
        <button
          onClick={() => setView('home')}
          aria-label="Back"
          style={{
            width: 48, height: 48, flexShrink: 0,
            background: '#20304E', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 12,
            color: '#e8e8ea', fontSize: '1.35rem', cursor: 'pointer', lineHeight: 1,
          }}
        >
          &#8592;
        </button>
        <h1 style={{
          flex: 1, textAlign: 'center', margin: 0,
          fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900,
          fontSize: '1.25rem', letterSpacing: '-0.02em', color: '#fff',
        }}>
          LEADERBOARD
        </h1>
        <div style={{ width: 40, flexShrink: 0 }} />
      </div>

      {/* Mode boards: one ladder per rule set, and one for two-player rooms,
          named the way Home names them. SURVIVAL wears its coral. The row
          spans the column edge to edge, like the period tabs under it. */}
      <div style={{ display: 'flex', gap: 6, width: '100%', marginBottom: 8, boxSizing: 'border-box' }}>
        {(['CLIMB', 'RUSH', 'SURVIVAL', 'DAILY', 'VERSUS'] as const).map(m => {
          const on = mode === m;
          const accent = m === 'SURVIVAL' ? '#F43F5E' : '#3E7BFA';
          return (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                // Five boards share one row down to a 320 px phone: each tab
                // takes its label's width plus an even share of the rest, and
                // a label never wraps onto a second line.
                flex: '1 1 auto', padding: '0 4px', whiteSpace: 'nowrap',
                height: 44, borderRadius: 10,
                background: on ? `linear-gradient(140deg, ${accent}30 0%, transparent 70%), #1C2A45` : '#16223A',
                border: on ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.12)',
                boxShadow: on ? `0 0 14px ${accent}44` : 'none',
                color: on ? '#fff' : '#C4C4D2',
                fontFamily: 'var(--font-heading)', fontWeight: 800,
                // Scales down on the narrowest phones, never under the 11 px
                // floor the rest of the app keeps.
                fontSize: 'clamp(0.6875rem, 3.5vw, 0.8rem)', letterSpacing: '0.03em',
                cursor: 'pointer', transition: 'all 0.15s ease',
              }}
            >
              {m === 'VERSUS' ? '1 VS 1' : m}
            </button>
          );
        })}
      </div>

      <div className="lb-filters">
        <select
          value={realm}
          onChange={(e) => setRealm(e.target.value)}
          className="lb-select"
          style={{
            appearance: 'none', WebkitAppearance: 'none',
            background: '#16223A', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 12,
            color: '#ECECF2', padding: '12px 14px', fontFamily: 'var(--font-sans)', fontWeight: 700,
            fontSize: '0.9rem', letterSpacing: '0.04em', cursor: 'pointer',
          }}
        >
          <option value="GLOBAL">ALL REALMS</option>
          {Object.entries(REALMS).map(([k, v]) => (
            <option key={k} value={k}>{v.name.toUpperCase()}</option>
          ))}
        </select>

        <div className="lb-tabs" style={{ display: 'flex', gap: 6, background: 'none', padding: 0 }}>
          {([['daily', 'TODAY'], ['weekly', 'WEEK'], ['alltime', 'ALL TIME']] as const).map(([key, text]) => (
            <button
              key={key}
              onClick={() => { periodTouched.current = true; setPeriod(key); }}
              style={{
                flex: 1, height: 44, borderRadius: 10,
                background: period === key ? 'linear-gradient(140deg, rgba(62,123,250,0.28) 0%, transparent 70%), #1C2A45' : '#16223A',
                border: period === key ? '2px solid #3E7BFA' : '1px solid rgba(255,255,255,0.12)',
                boxShadow: period === key ? '0 0 14px rgba(62,123,250,0.28)' : 'none',
                color: period === key ? '#fff' : '#C4C4D2',
                fontFamily: 'var(--font-heading)', fontWeight: 800,
                fontSize: '0.9rem', letterSpacing: '0.08em',
                cursor: 'pointer', transition: 'all 0.15s ease',
              }}
            >
              {text}
            </button>
          ))}
        </div>
      </div>

      <div className="lb-board">
        {loading ? (
          <div className="lb-empty">LOADING RANKINGS...</div>
        ) : leaders.length === 0 ? (
          <div className="lb-empty">No rankings yet. Play a match to open the board.</div>
        ) : (
          leaders.map((p, i) => {
            const nick = p.nickname.toUpperCase();
            const open = expanded === nick;
            const hasBands = p.easy_total + p.medium_total + p.hard_total > 0;
            return (
              <div key={nick + i}>
                <div
                  className={`lb-row ${nick === displayNick ? 'is-me' : ''}`}
                  onClick={() => setExpanded(open ? null : nick)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="lb-rank">{renderBadge(i)}</div>
                  <div className="lb-player">
                    <span className="lb-name">{nick}</span>
                    <span className="lb-stats">{statLine(p)}</span>
                  </div>
                  <div className="lb-score">
                    {mode === 'SURVIVAL'
                      ? <>{p.depth} <span className="pts">DEEP</span></>
                      : <>{p.total_score.toLocaleString('en-US')} <span className="pts">PTS</span></>}
                  </div>
                </div>
                {/* The player card: the row states the ranked number, the
                    card answers "how do they actually play". Bands only
                    exist for answers counted since accuracy tracking began,
                    so a legacy player opens to an honest explanation. */}
                {open && (
                  <div style={{
                    margin: '2px 0 6px', padding: '10px 14px',
                    background: '#182541', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12,
                    display: 'flex', flexDirection: 'column', gap: 7,
                  }}>
                    {hasBands ? (
                      <>
                        {bandRow('easy', p.easy_correct, p.easy_total)}
                        {bandRow('medium', p.medium_correct, p.medium_total)}
                        {bandRow('hard', p.hard_correct, p.hard_total)}
                        {p.avgSec !== null && (
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.1em', color: '#b6b6be' }}>
                            AVG ANSWER TIME {p.avgSec.toFixed(1)}S
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: '#b6b6be' }}>
                        Band stats grow as they play. Nothing recorded here yet.
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* MY RANK SECTION — hidden while the board itself is empty
          (a second empty-state message on top of the first reads broken) */}
      {!loading && leaders.length > 0 && (
        <div className="my-rank-banner">
          {myRank ? (
            <>
              <div className="my-rank-label">YOUR RANK</div>
              <div className="my-rank-values">
                <div className="lb-rank">{renderBadge(myRank - 1)}</div>
                <div className="lb-player">
                  <span className="lb-name">{displayNick}</span>
                  <span className="lb-stats">{statLine(myScoreData)}</span>
                </div>
                <div className="lb-score" style={{ color: '#14B8A6' }}>
                  {mode === 'SURVIVAL'
                    ? <>{myScoreData.depth} <span className="pts">DEEP</span></>
                    : <>{myScoreData.total_score.toLocaleString('en-US')} <span className="pts">PTS</span></>}
                </div>
              </div>
            </>
          ) : (
            <div className="my-rank-unranked">
              AWAITING CALIBRATION... PLAY MATCHES TO ENTER RANKINGS.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
